import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router';
import styles from './SchoolInformation.module.css';
import Button from 'components/Button/Button';
import {
    faInstagram,
    faLinkedin,
    faFacebook,
    faYoutube
} from '@fortawesome/free-brands-svg-icons';
import {
    faEnvelope,
    faPhone,
    faGlobe,
    faInfoCircle,
    faUserTie,
    faGraduationCap,
    faIdCard,
    faShieldAlt,
    faMapMarkerAlt,
    faCalendarAlt,
    faFileExport,
    faLock
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import SocialMediaIcon from 'components/SocialMediaIcon/SocialMediaIcon';
import { api } from 'api/axios';
import { useAuth } from 'hooks/auth';
import { UserRole } from 'types/auth';
import Loading from 'components/Loading/Loading';

const SchoolInformation: React.FC = () => {
    const navigate = useNavigate();
    const auth = useAuth();
    const { pathname } = useLocation();
    const { id } = useParams();

    const userRole = auth?.user?.role;
    const [school, setSchool] = useState<any>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const [devices, setDevices] = useState<any[] | null>(null);
    const [loadingDevices, setLoadingDevices] = useState(false);
    const [devicesError, setDevicesError] = useState<string | null>(null);

    const fetchDevices = async () => {
        try {
            setLoadingDevices(true);
            setDevicesError(null);
            const endpoint = id ? `/schools/${id}/devices` : `/schools/my-school/devices`;
            const response = await api.get(endpoint);
            setDevices(response.data.data);
        } catch (err: any) {
            console.error('Error fetching devices:', err);
            setDevicesError(err.response?.data?.message || 'Failed to fetch devices');
        } finally {
            setLoadingDevices(false);
        }
    };

    const handleEditClick = () => {
        if (userRole === UserRole.SUPER_ADMIN) {
            navigate(`/school-management/edit/${school.institutional_id}`);
        } else {
            navigate('/update-school-information');
        }
    };

    useEffect(() => {
        if (userRole === UserRole.SUPER_ADMIN && pathname === '/school-information') {
            navigate('/school-management', { replace: true });
            return;
        }

        const fetchSchool = async () => {
            try {
                setIsLoading(true);
                const endpoint = id ? `/schools/${id}` : '/schools/my-school';
                const response = await api.get(endpoint);
                setSchool(response.data.data);
                setError(null);
            } catch (err: any) {
                console.error('Error fetching school information:', err);
                setError(err.response?.data?.message || 'Failed to fetch school information');
            } finally {
                setIsLoading(false);
            }
        };

        fetchSchool();
    }, [id, userRole, pathname, navigate]);

    if (isLoading) return <Loading fullPage message="Loading school information..." />;
    if (error) return <div className={styles.container}>Error: {error}</div>;
    if (!school) return <div className={styles.container}>No school information found.</div>;

    const stats = [
        { label: 'Total Students', value: (school.student_count || 0).toLocaleString() },
        { label: 'Faculty Members', value: '0' },
        // { label: 'Departments', value: '14' },
        // { label: 'School Grade', value: 'A+' }
    ];

    return (
        <div className={styles.container}>
            {/* Breadcrumb */}
            <div className={styles.breadcrumb}>
                <span className={styles.breadcrumbLink}>Management</span>
                <span className={styles.breadcrumbSeparator}>›</span>
                <span className={styles.breadcrumbCurrent}>School Profile</span>
            </div>

            {/* Header Card */}
            <header className={styles.headerCard}>
                <div className={styles.headerLeft}>
                    <div className={styles.logoWrapper}>
                        <img src={school.logo_url} alt="School Logo" className={styles.schoolLogo} />
                    </div>
                    <div className={styles.headerInfo}>
                        <div className={styles.titleRow}>
                            <h1 className={styles.schoolName}>{school.name}</h1>
                            <span className={styles.statusBadge}>
                                <span className={styles.statusDot}></span> LIVE
                            </span>
                        </div>
                        <div className={styles.secondaryInfo}>
                            <span className={styles.infoLine}>
                                <FontAwesomeIcon className={styles.infoIcon} icon={faIdCard} /> Account ID : #{school.institutional_id}
                            </span>
                            {/* <span className={styles.infoLine}>
                                <FontAwesomeIcon className={styles.infoIcon} icon={faIdCard} /> Institutional ID: N/A
                            </span> */}
                            <span className={styles.infoLine}>
                                <FontAwesomeIcon className={styles.infoIcon} icon={faMapMarkerAlt} /> {[school.address, school.country].filter(Boolean).join(', ') || 'Address not provided'}
                            </span>
                        </div>
                    </div>
                </div>
                <div className={styles.headerActions}>
                    {(userRole === UserRole.SCHOOL_ADMIN || userRole === UserRole.SUPER_ADMIN) && (
                        <Button className={styles.editBtn} onClick={handleEditClick}>
                            Edit Profile
                        </Button>
                    )}
                </div>
            </header>

            {/* Institutional Overview */}
            <section className={styles.institutionalOverview}>
                <h2 className={styles.overviewTitle}>Institutional Overview</h2>
                <div className={styles.statsGrid}>
                    {stats.map((stat, index) => (
                        <div key={index} className={styles.statItem}>
                            <span className={styles.statNumber}>{stat.value}</span>
                            <span className={styles.statLabel}>{stat.label}</span>
                        </div>
                    ))}
                </div>
            </section>

            {/* Three Column Details */}
            <div className={styles.detailsGrid}>
                {/* School Details */}
                <div className={styles.card}>
                    <div className={styles.cardHeader}>
                        <h3 className={styles.cardTitle}>School Details</h3>
                        <FontAwesomeIcon icon={faInfoCircle} className={styles.cardHeaderIcon} />
                    </div>
                    <div className={styles.cardContent}>
                        <div className={styles.infoItem}>
                            <span className={styles.infoLabel}>Official Email</span>
                            <div className={styles.infoValue}>
                                <div className={styles.iconBox}>
                                    <FontAwesomeIcon icon={faEnvelope} />
                                </div>
                                <a href={`mailto:${school.email}`}>{school.email || 'N/A'}</a>
                            </div>
                        </div>
                        <div className={styles.infoItem}>
                            <span className={styles.infoLabel}>Office Number</span>
                            <div className={styles.infoValue}>
                                <div className={styles.iconBox}>
                                    <FontAwesomeIcon icon={faPhone} />
                                </div>
                                <span>{school.contact || 'N/A'}</span>
                            </div>
                        </div>
                        <div className={styles.infoItem}>
                            <span className={styles.infoLabel}>Website</span>
                            <div className={styles.infoValue}>
                                <div className={styles.iconBox}>
                                    <FontAwesomeIcon icon={faGlobe} />
                                </div>
                                {school.website_url ? (
                                    <a href={school.website_url} target="_blank" rel="noreferrer">
                                        {school.website_url.replace(/^https?:\/\//, '')}
                                    </a>
                                ) : 'N/A'}
                            </div>
                        </div>
                        <div className={styles.infoItem}>
                            <span className={styles.infoLabel}>Social Connections</span>
                            <div className={styles.socialRow}>
                                {school.facebook_link && (
                                    <a href={school.facebook_link} target="_blank" rel="noreferrer" className={styles.socialIconBtn}>
                                        <FontAwesomeIcon icon={faFacebook} />
                                    </a>
                                )}
                                {school.linkedin_link && (
                                    <a href={school.linkedin_link} target="_blank" rel="noreferrer" className={styles.socialIconBtn}>
                                        <FontAwesomeIcon icon={faLinkedin} />
                                    </a>
                                )}
                                {school.instagram_link && (
                                    <a href={school.instagram_link} target="_blank" rel="noreferrer" className={styles.socialIconBtn}>
                                        <FontAwesomeIcon icon={faInstagram} />
                                    </a>
                                )}
                                {school.youtube_link && (
                                    <a href={school.youtube_link} target="_blank" rel="noreferrer" className={styles.socialIconBtn}>
                                        <FontAwesomeIcon icon={faYoutube} />
                                    </a>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Principal Profile */}
                <div className={styles.card}>
                    <div className={styles.cardHeader}>
                        <h3 className={styles.cardTitle}>Principal Profile</h3>
                        <FontAwesomeIcon icon={faShieldAlt} className={styles.cardHeaderIcon} />
                    </div>
                    <div className={styles.cardContent}>
                        <div className={styles.profilePicWrapper}>
                            <div className={styles.profileCircle}>
                                <FontAwesomeIcon icon={faUserTie} />
                            </div>
                            <div className={styles.profileNameBox}>
                                <h4 className={styles.profileName}>{school.principal_name || 'N/A'}</h4>
                            </div>
                        </div>
                        <div className={styles.principalDetails}>
                            {/* <div className={styles.detailRow}>
                                <div className={styles.iconBox}><FontAwesomeIcon icon={faGraduationCap} /></div>
                                <div className={styles.detailContent}>
                                    <span className={styles.detailLabel}>Qualification</span>
                                    <span className={styles.detailValue}>N/A</span>
                                </div>
                            </div> */}
                            <div className={styles.detailRow}>
                                <div className={styles.iconBox}><FontAwesomeIcon icon={faEnvelope} /></div>
                                <div className={styles.detailContent}>
                                    <span className={styles.infoLabel}>Email</span>
                                    <span className={styles.infoValue}>{school.principal_email || 'N/A'}</span>
                                </div>
                            </div>
                            <div className={styles.detailRow}>
                                <div className={styles.iconBox}><FontAwesomeIcon icon={faPhone} /></div>
                                <div className={styles.detailContent}>
                                    <span className={styles.infoLabel}>Contact</span>
                                    <span className={styles.infoValue}>{school.principal_contact || 'N/A'}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Administrative Account */}
                {(userRole === UserRole.SUPER_ADMIN || userRole === UserRole.SCHOOL_ADMIN) &&
                    <div className={styles.card}>
                        <div className={styles.cardHeader}>
                            <h3 className={styles.cardTitle}>Administrative Account</h3>
                            <FontAwesomeIcon icon={faLock} className={styles.cardHeaderIcon} />
                        </div>
                        <div className={styles.cardContent}>
                            {school.admin ? (
                                <>
                                    <div className={styles.profilePicWrapper}>
                                        <div className={styles.profileCircle}>
                                            <FontAwesomeIcon icon={faUserTie} />
                                        </div>
                                        <div className={styles.profileNameBox}>
                                            <h4 className={styles.profileName}>{school.admin.admin_name}</h4>
                                        </div>
                                    </div>
                                    <div className={styles.principalDetails}>
                                        <div className={styles.detailRow}>
                                            <div className={styles.iconBox}><FontAwesomeIcon icon={faIdCard} /></div>
                                            <div className={styles.detailContent}>
                                                <span className={styles.infoLabel}>Username</span>
                                                <span className={styles.infoValue}>{school.admin.admin_username}</span>
                                            </div>
                                        </div>
                                        <div className={styles.detailRow}>
                                            <div className={styles.iconBox}><FontAwesomeIcon icon={faEnvelope} /></div>
                                            <div className={styles.detailContent}>
                                                <span className={styles.infoLabel}>Email</span>
                                                <span className={styles.infoValue}>{school.admin.admin_email}</span>
                                            </div>
                                        </div>
                                    </div>
                                </>
                            ) : (
                                <div className={styles.emptyContainer}>
                                    No administrator assigned.
                                </div>
                            )}
                        </div>
                        {/* <div className={styles.cardFooter}>
                            <Button variant="ghost" size="sm" className={styles.accessLogsBtn}>
                                View Access Logs
                            </Button>
                        </div> */}
                    </div>
                }
            </div>

            {/* Device Management Section */}
            {(userRole === UserRole.SUPER_ADMIN || userRole === UserRole.SCHOOL_ADMIN) && (
                <section className={styles.deviceManagement}>
                    <h2 className={styles.overviewTitle}>Device Management</h2>
                    
                    <div className={styles.statsGrid} style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
                        <div className={styles.statItem}>
                            <span className={styles.statNumber}>{school.device_limit || 0}</span>
                            <span className={styles.statLabel}>Device Limit</span>
                        </div>
                        <div className={styles.statItem}>
                            <span className={styles.statNumber} style={{ fontSize: '24px' }}>
                                {school.device_expiry ? new Date(school.device_expiry).toLocaleDateString() : 'N/A'}
                            </span>
                            <span className={styles.statLabel}>License Expiry</span>
                        </div>
                        <div className={styles.statItem}>
                            <span className={styles.statNumber}>{school.device_count || 0}</span>
                            <span className={styles.statLabel}>Registered Devices</span>
                        </div>
                    </div>

                    <div className={styles.card}>
                        <div className={styles.cardHeader}>
                            <h3 className={styles.cardTitle}>Registered Devices List</h3>
                            <FontAwesomeIcon icon={faLock} className={styles.cardHeaderIcon} />
                        </div>
                        <div className={styles.cardContent} style={{ padding: '0' }}>
                            {!devices && !loadingDevices && (
                                <div className={styles.emptyContainer} style={{ padding: '24px', textAlign: 'center' }}>
                                    <Button onClick={fetchDevices} className={styles.editBtn}>Show Devices List</Button>
                                </div>
                            )}
                            {loadingDevices && (
                                <div className={styles.emptyContainer} style={{ padding: '24px', textAlign: 'center' }}>
                                    <Loading message="Loading devices..." />
                                </div>
                            )}
                            {devicesError && (
                                <div className={styles.emptyContainer} style={{ padding: '24px', textAlign: 'center', color: 'red' }}>
                                    {devicesError}
                                </div>
                            )}
                            {devices && devices.length > 0 && (
                                <table className={styles.deviceTable}>
                                    <thead>
                                        <tr>
                                            <th>Device ID</th>
                                            <th>Registered On</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {devices.map((device: any, index: number) => (
                                            <tr key={index}>
                                                <td>{device.device_id}</td>
                                                <td>{new Date(device.registered_at).toLocaleString()}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                            {devices && devices.length === 0 && (
                                <div className={styles.emptyContainer} style={{ padding: '24px', textAlign: 'center', color: '#616F89' }}>
                                    No devices registered yet.
                                </div>
                            )}
                        </div>
                    </div>
                </section>
            )}

        </div>
    );
};

export default SchoolInformation;
